<script setup lang="ts">
import {
  ref, watch, onUnmounted,
} from 'vue';
import type { Component } from 'vue';
import ConnxEmployeeManagementActionsDelete from './ConnxEmployeeManagementActionsDelete.vue';
import ConnxEmployeeManagementActionsChangeEmpCode from './ConnxEmployeeManagementActionsChangeEmpCode.vue';
import ConnxEmployeeManagementActionsDataTransfer from './ConnxEmployeeManagementActionsDataTransfer.vue';
import useEmployeeManagementActions from '@/stores/Employee/employee-management-actions';
import useEmitter from '@/composables/emitter';
import useApplicationStore from '@/stores/application';

const action = ref<string>('');
const visible = ref(false);
const employeeCode = ref<string | null>(null);
const emitter = useEmitter();
const employeeManagementActions = useEmployeeManagementActions();
const applicationStore = useApplicationStore();

const componentMap = new Map<string, Component>([
  ['delete', ConnxEmployeeManagementActionsDelete],
  ['change', ConnxEmployeeManagementActionsChangeEmpCode],
  ['transfer', ConnxEmployeeManagementActionsDataTransfer],
]);

const hideComponent = () => {
  const empCode = employeeManagementActions.newEmpCode ? employeeManagementActions.newEmpCode : employeeManagementActions.employee?.empCode;
  visible.value = false;
  employeeCode.value = null;
  employeeManagementActions.newEmpCode = '';
  employeeManagementActions.clearEmployeeDetails();
  emitter.emit('ConnxEmployeeManagementActions:hideComponent', empCode);
};

const showComponent = async (empCode: string, component: 'delete' | 'change' | 'transfer') => {
  applicationStore.setLoading(true);
  await employeeManagementActions.init(empCode);
  applicationStore.setLoading(false);
  employeeCode.value = empCode;
  visible.value = true;
  action.value = component;
};

emitter.on('ConnxEmployeeManagementActions:showComponentDelete', (empCode: string) => showComponent(empCode, 'delete'));
emitter.on('ConnxEmployeeManagementActions:showComponentDataTransfer', (empCode: string) => showComponent(empCode, 'transfer'));
emitter.on('ConnxEmployeeManagementActions:showComponentChangeEmpCode', (empCode: string) => showComponent(empCode, 'change'));

const escapeEventListener = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    hideComponent();
  }
};

watch(() => visible.value, () => {
  if (visible.value) {
    document.addEventListener('keyup', escapeEventListener);
  } else {
    document.removeEventListener('keyup', escapeEventListener);
  }
});

onUnmounted(() => {
  if (visible.value) {
    document.removeEventListener('keyup', escapeEventListener);
  }
});
</script>

<template>
  <Teleport
    v-if="visible"
    to="body"
  >
    <div class="tailwindcss">
      <component
        :is="componentMap.get(action)"
        :employee-code="employeeCode"
        @close="hideComponent()"
      />
    </div>
  </Teleport>
</template>
